import { useEffect, useMemo, useRef, useState } from "react";
import { testimonials } from "../../data/homeData";
import { useExperience } from "../../../../../shared/context/ExperienceContext";
import { Icons } from "../../../../../shared/icons/icons";
import SectionHeading from "../../../../../shared/components/ui/SectionHeading/SectionHeading";
import "./TestimonialsSection.css";

const MIN_CAROUSEL_ITEMS = 4;
const RESUME_DELAY_MS = 500;
const SWIPE_HINT_REAPPEAR_MS = 10000;

const testimonialSectionByMode = {
  all: {
    eyebrow: "Depoimentos",
    title: "O que dizem sobre a experiência com a Dionara",
    description:
      "Relatos reais de quem viveu uma experiência de cuidado, atenção aos detalhes e acompanhamento mais leve e personalizado.",
  },
  aesthetics: {
    eyebrow: "Depoimentos",
    title: "O que dizem sobre os cuidados estéticos",
    description:
      "Relatos de pacientes que buscaram autoestima, bem-estar e resultados mais naturais com um atendimento acolhedor e personalizado.",
  },
  nutrition: {
    eyebrow: "Depoimentos",
    title: "O que dizem sobre o acompanhamento nutricional",
    description:
      "Relatos de pacientes que viveram um processo mais leve, estratégico e sustentável na busca por saúde, equilíbrio e constância.",
  },
};

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function renderStars(rating = 5) {
  return Array.from({ length: 5 }, (_, index) => (
    <Icons.Star
      key={index}
      className={`testimonial-card__star ${index < rating ? "is-filled" : ""}`}
      aria-hidden="true"
    />
  ));
}

function SourceIcon() {
  return <Icons.FcGoogle aria-hidden="true" />;
}

export default function TestimonialsSection() {
  const { mode, filterByMode } = useExperience();

  const sectionRef = useRef(null);
  const measureRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const swipeHintTimeoutRef = useRef(null);
  const textRefs = useRef([]);

  const [useCarousel, setUseCarousel] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [truncatedMap, setTruncatedMap] = useState({});
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isSectionInView, setIsSectionInView] = useState(false);
  const [isSwipeHintVisible, setIsSwipeHintVisible] = useState(true);

  const sectionContent =
    testimonialSectionByMode[mode] || testimonialSectionByMode.all;

  const shouldPauseCarousel = isCarouselPaused || Boolean(selectedTestimonial);

  const shouldUseManualSwipe = isTouchDevice && useCarousel;
  const shouldUseAutoCarousel = useCarousel && !shouldUseManualSwipe;

  const filteredTestimonials = useMemo(() => {
    if (typeof filterByMode === "function") {
      return filterByMode(testimonials);
    }

    return testimonials;
  }, [filterByMode]);

  const shouldShowSwipeHint =
    isTouchDevice &&
    useCarousel &&
    isSectionInView &&
    isSwipeHintVisible &&
    !selectedTestimonial;

  useEffect(() => {
    setSelectedTestimonial(null);
    setIsSwipeHintVisible(true);
  }, [mode]);

  useEffect(() => {
    const updateInputMode = () => {
      const hasTouch =
        window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
        window.innerWidth <= 768;

      setIsTouchDevice(hasTouch);
    };

    updateInputMode();
    window.addEventListener("resize", updateInputMode);

    return () => {
      window.removeEventListener("resize", updateInputMode);
    };
  }, []);

  useEffect(() => {
    const checkLayout = () => {
      const measureEl = measureRef.current;
      if (!measureEl) return;

      const viewport = measureEl.parentElement;
      if (!viewport) return;

      const contentWidth = measureEl.scrollWidth;
      const viewportWidth = viewport.clientWidth;

      const hasOverflow = contentWidth > viewportWidth + 4;
      const hasMinimumCardsForCarousel =
        filteredTestimonials.length >= MIN_CAROUSEL_ITEMS;

      setUseCarousel(hasMinimumCardsForCarousel || hasOverflow);
    };

    const timer = window.setTimeout(checkLayout, 150);
    window.addEventListener("resize", checkLayout);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", checkLayout);
    };
  }, [filteredTestimonials]);

  useEffect(() => {
    const sectionEl = sectionRef.current;
    if (!sectionEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting && entry.intersectionRatio > 0.24;

        setIsSectionInView(isVisible);

        if (isVisible) {
          setIsSwipeHintVisible(true);
        }
      },
      {
        threshold: [0, 0.24, 0.5],
      }
    );

    observer.observe(sectionEl);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    if (selectedTestimonial) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [selectedTestimonial]);

  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) {
        window.clearTimeout(resumeTimeoutRef.current);
      }

      if (swipeHintTimeoutRef.current) {
        window.clearTimeout(swipeHintTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    textRefs.current = [];

    const checkTruncation = () => {
      const nextMap = {};

      filteredTestimonials.forEach((testimonial, index) => {
        const el = textRefs.current[index];
        if (!el) return;

        const isTruncated =
          el.scrollHeight > el.clientHeight + 1 ||
          el.scrollWidth > el.clientWidth + 1;

        nextMap[index] = isTruncated;
      });

      setTruncatedMap(nextMap);
    };

    const timer = window.setTimeout(checkTruncation, 180);
    window.addEventListener("resize", checkTruncation);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", checkTruncation);
    };
  }, [useCarousel, filteredTestimonials]);

  const hideSwipeHintTemporarily = () => {
    if (!isTouchDevice) return;

    setIsSwipeHintVisible(false);

    if (swipeHintTimeoutRef.current) {
      window.clearTimeout(swipeHintTimeoutRef.current);
    }

    swipeHintTimeoutRef.current = window.setTimeout(() => {
      if (isSectionInView) {
        setIsSwipeHintVisible(true);
      }

      swipeHintTimeoutRef.current = null;
    }, SWIPE_HINT_REAPPEAR_MS);
  };

  const openModal = (testimonial) => {
    if (resumeTimeoutRef.current) {
      window.clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }

    setSelectedTestimonial(testimonial);
    setIsSwipeHintVisible(false);
  };

  const closeModal = () => {
    setSelectedTestimonial(null);

    if (resumeTimeoutRef.current) {
      window.clearTimeout(resumeTimeoutRef.current);
    }

    resumeTimeoutRef.current = window.setTimeout(() => {
      setIsCarouselPaused(false);
      resumeTimeoutRef.current = null;
    }, RESUME_DELAY_MS);
  };

  const handleCarouselMouseEnter = () => {
    if (!shouldUseAutoCarousel) return;

    if (resumeTimeoutRef.current) {
      window.clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }

    setIsCarouselPaused(true);
  };

  const handleCarouselMouseLeave = () => {
    if (!shouldUseAutoCarousel) return;
    if (selectedTestimonial) return;

    if (resumeTimeoutRef.current) {
      window.clearTimeout(resumeTimeoutRef.current);
    }

    resumeTimeoutRef.current = window.setTimeout(() => {
      setIsCarouselPaused(false);
      resumeTimeoutRef.current = null;
    }, RESUME_DELAY_MS);
  };

  const renderCard = (testimonial, index, keyPrefix = "") => {
    const initials = getInitials(testimonial.name);
    const hasAvatar = Boolean(testimonial.avatar);
    const originalIndex = index % filteredTestimonials.length;
    const isMeasureCard = keyPrefix === "measure-";

    const actionLabel = truncatedMap[originalIndex]
      ? isTouchDevice
        ? "Toque para ler completo"
        : "Clique para ler completo"
      : isTouchDevice
        ? "Toque para abrir"
        : "Clique para ver";

    return (
      <article
        className="testimonial-card"
        key={`${keyPrefix}${testimonial.name}-${index}`}
        onClick={() => openModal(testimonial)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openModal(testimonial);
          }
        }}
      >
        <div className="testimonial-card__top">
          <div className="testimonial-card__header">
            <div className="testimonial-card__avatar">
              {hasAvatar ? (
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="testimonial-card__avatar-image"
                  draggable="false"
                />
              ) : (
                <span className="testimonial-card__avatar-fallback">
                  {initials || "P"}
                </span>
              )}
            </div>

            <div className="testimonial-card__meta">
              <strong className="testimonial-card__name">
                {testimonial.name}
              </strong>

              <span className="testimonial-card__date">
                {testimonial.date || "Avaliação"}
              </span>
            </div>
          </div>

          <div
            className="testimonial-card__source"
            aria-label="Depoimento verificado"
            title="Depoimento verificado"
          >
            <SourceIcon />
          </div>
        </div>

        <div
          className="testimonial-card__rating"
          aria-label={`${testimonial.rating || 5} de 5 estrelas`}
        >
          {renderStars(testimonial.rating || 5)}
        </div>

        <p
          className="testimonial-card__text"
          ref={(el) => {
            if (!isMeasureCard && keyPrefix === "loop-a-") {
              textRefs.current[originalIndex] = el;
            }

            if (!isMeasureCard && !useCarousel) {
              textRefs.current[originalIndex] = el;
            }

            if (!isMeasureCard && keyPrefix === "static-") {
              textRefs.current[originalIndex] = el;
            }
          }}
        >
          {testimonial.text}
        </p>

        <span className="testimonial-card__action">
          {actionLabel}
          <Icons.ArrowRight />
        </span>
      </article>
    );
  };

  const renderCarouselGroup = (groupKey = "loop-a-") => (
    <div
      className="testimonials-carousel-group"
      aria-hidden={groupKey !== "loop-a-"}
    >
      {filteredTestimonials.map((testimonial, index) =>
        renderCard(testimonial, index, groupKey)
      )}
    </div>
  );

  if (!filteredTestimonials.length) {
    return null;
  }

  return (
    <section className="testimonials-section" ref={sectionRef}>
      <div className="container">
        <div className="testimonials-section__heading-wrap">
          <SectionHeading
            eyebrow={sectionContent.eyebrow}
            title={sectionContent.title}
            description={sectionContent.description}
            className="section-heading--testimonials center"
          />
        </div>

        <div
          className={`testimonials-carousel-wrapper ${
            useCarousel ? "is-carousel" : "is-static"
          } ${shouldUseManualSwipe ? "is-manual-swipe" : ""}`}
          onMouseEnter={handleCarouselMouseEnter}
          onMouseLeave={handleCarouselMouseLeave}
        >
          <div className="testimonials-carousel-fade testimonials-carousel-fade--left" />
          <div className="testimonials-carousel-fade testimonials-carousel-fade--right" />

          <div
            className="testimonials-carousel-viewport"
            onScroll={hideSwipeHintTemporarily}
            onPointerDown={hideSwipeHintTemporarily}
            onTouchStart={hideSwipeHintTemporarily}
          >
            {shouldUseAutoCarousel ? (
              <div
                className={`testimonials-carousel-track is-animated ${
                  shouldPauseCarousel ? "is-paused" : ""
                }`}
              >
                {renderCarouselGroup("loop-a-")}
                {renderCarouselGroup("loop-b-")}
              </div>
            ) : (
              <div className="testimonials-carousel-track">
                <div className="testimonials-carousel-group">
                  {filteredTestimonials.map((testimonial, index) =>
                    renderCard(
                      testimonial,
                      index,
                      shouldUseManualSwipe ? "loop-a-" : "static-"
                    )
                  )}
                </div>
              </div>
            )}

            <div className="testimonials-carousel-measure" ref={measureRef}>
              {filteredTestimonials.map((testimonial, index) =>
                renderCard(testimonial, index, "measure-")
              )}
            </div>
          </div>

          <div
            className={`testimonials-mobile-hint ${
              shouldShowSwipeHint ? "is-visible" : ""
            }`}
            aria-hidden="true"
          >
            <span className="testimonials-mobile-hint__text">
              Arraste para o lado
            </span>

            <span className="testimonials-mobile-hint__arrows">
              <span />
              <span />
              <span />
            </span>
          </div>
        </div>
      </div>

      {selectedTestimonial && (
        <div className="testimonial-modal" onClick={closeModal}>
          <div
            className="testimonial-modal__dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="testimonial-modal__close"
              onClick={closeModal}
              aria-label="Fechar depoimento"
            >
              <Icons.X />
            </button>

            <div className="testimonial-modal__header">
              <div className="testimonial-modal__profile">
                <div className="testimonial-modal__avatar">
                  {selectedTestimonial.avatar ? (
                    <img
                      src={selectedTestimonial.avatar}
                      alt={selectedTestimonial.name}
                      className="testimonial-modal__avatar-image"
                    />
                  ) : (
                    <span className="testimonial-modal__avatar-fallback">
                      {getInitials(selectedTestimonial.name) || "P"}
                    </span>
                  )}
                </div>

                <div className="testimonial-modal__meta">
                  <strong className="testimonial-modal__name">
                    {selectedTestimonial.name}
                  </strong>

                  <span className="testimonial-modal__date">
                    {selectedTestimonial.date || "Avaliação"}
                  </span>
                </div>
              </div>

              <div className="testimonial-modal__source">
                <SourceIcon />
              </div>
            </div>

            <div
              className="testimonial-modal__rating"
              aria-label={`${selectedTestimonial.rating || 5} de 5 estrelas`}
            >
              {renderStars(selectedTestimonial.rating || 5)}
            </div>

            <p className="testimonial-modal__text">
              {selectedTestimonial.text}
            </p>

            {selectedTestimonial.reviewUrl && (
              <a
                href={selectedTestimonial.reviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="testimonial-modal__link"
              >
                Ver avaliação original
                <Icons.ArrowRight />
              </a>
            )}
          </div>
        </div>
      )}
    </section>
  );
}